import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    User, MapPin, Phone, Mail, Save, Loader2, Upload,
    Star, FileText, Award, Calendar, ExternalLink,
    Briefcase, Globe, Shield, CheckCircle, Edit3, ChevronRight
} from 'lucide-react';
import QRCodeSection from '../../components/qr/QRCodeSection';
import ProfileReseauSection from './ProfileReseauSection';
import PersonalEmailCard from '../../components/profile/PersonalEmailCard';

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const SPECIALTIES = [
    'Appartement', 'Maison', 'Luxe / Prestige', 'Investissement locatif',
    'Primo-accédant', 'Terrain', 'Commerce / Local', 'Neuf / VEFA', 'Viager'
];

const generateSlug = (name) => {
    let slug = (name || '').toLowerCase().trim();
    slug = slug.replace(/[àáâãäå]/g, 'a').replace(/[èéêë]/g, 'e');
    slug = slug.replace(/[ìíîï]/g, 'i').replace(/[òóôõö]/g, 'o');
    slug = slug.replace(/[ùúûü]/g, 'u').replace(/[ç]/g, 'c');
    slug = slug.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return slug;
};

function ProfilePage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [editing, setEditing] = useState(false);
    const [geocoding, setGeocoding] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [stats, setStats] = useState({ mandates: 0, badges: 0, rating: 0, xp: 0 });
    const [profile, setProfile] = useState({
        city: '', bio: '', zones: '', public_phone: '',
        years_experience: '', specialties: [], coverage_radius_km: 20,
        latitude: null, longitude: null
    });
    const photoRef = useRef(null);

    useEffect(() => {
        loadAll();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadAll = async () => {
        try {
            const [profileRes, statsRes, badgesRes] = await Promise.allSettled([
                api.get('/users/me/profile'),
                api.get('/reputation/agent/current/stats'),
                api.get('/reputation/agent/current/badges')
            ]);

            if (profileRes.status === 'fulfilled') {
                const d = profileRes.value.data;
                setProfile({
                    city: d.city || '',
                    bio: d.bio || '',
                    zones: (d.zones || []).join(', '),
                    public_phone: d.public_phone || d.phone || '',
                    years_experience: d.years_experience || '',
                    specialties: d.specialties || [],
                    coverage_radius_km: d.coverage_radius_km || 20,
                    latitude: d.latitude || null,
                    longitude: d.longitude || null
                });
                if (d.photo_storage_path) {
                    setPhotoPreview(`/api/users/photo/${d.photo_storage_path}`);
                }
            }

            const repStats = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
            const badgeCount = badgesRes.status === 'fulfilled' ? (badgesRes.value.data?.badges?.length || 0) : 0;

            setStats({
                mandates: repStats.total_mandates || 0,
                badges: badgeCount,
                rating: repStats.average_rating || 0,
                xp: repStats.season_xp || 0
            });
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const zonesArray = profile.zones.split(',').map(z => z.trim()).filter(Boolean);
            await api.put('/users/me/profile', {
                city: profile.city,
                bio: profile.bio,
                zones: zonesArray,
                public_phone: profile.public_phone,
                years_experience: parseInt(profile.years_experience) || 0,
                specialties: profile.specialties,
                coverage_radius_km: profile.coverage_radius_km,
                latitude: profile.latitude,
                longitude: profile.longitude
            });
            toast.success('Profil mis à jour');
            setEditing(false);
        } catch {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const geocodeCity = async (city) => {
        if (!city || city.length < 2) return;
        setGeocoding(true);
        try {
            const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(city)}&limit=1&type=municipality`);
            const data = await res.json();
            if (data.features?.length > 0) {
                const coords = data.features[0].geometry.coordinates;
                const name = data.features[0].properties.city || data.features[0].properties.name;
                setProfile(prev => ({ ...prev, city: name || prev.city, latitude: coords[1], longitude: coords[0] }));
                toast.success(`Position trouvée : ${name}`);
            } else {
                toast.error('Ville non trouvée');
            }
        } catch {
            toast.error('Erreur de géocodage');
        } finally {
            setGeocoding(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error('Image trop lourde (max 5 MB)'); return; }
        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post('/users/me/photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPhotoPreview(`/api/users/photo/${res.data.path}`);
            toast.success('Photo mise à jour');
        } catch {
            toast.error('Erreur upload photo');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const toggleSpecialty = (spec) => {
        setProfile(prev => ({
            ...prev,
            specialties: prev.specialties.includes(spec) ? prev.specialties.filter(s => s !== spec) : [...prev.specialties, spec]
        }));
    };

    const basePath = user?.role === 'admin' ? '/admin' : '/agent';

    if (loading) {
        return (
            <div className="space-y-6" data-testid="profile-page-loading">
                <Skeleton className="h-48 w-full bg-muted rounded-2xl" />
                <div className="grid lg:grid-cols-3 gap-6">
                    <Skeleton className="h-64 bg-muted rounded-2xl" />
                    <Skeleton className="h-64 bg-muted rounded-2xl lg:col-span-2" />
                </div>
            </div>
        );
    }

    return (
        <motion.div className="space-y-6" data-testid="profile-page" initial="hidden" animate="visible" variants={stagger}>

            {/* Hero Card */}
            <motion.div variants={fadeIn}>
                <div className="bg-gradient-to-br from-[#681793]/20 via-card to-[#FF914D]/10 rounded-2xl border border-border overflow-hidden">
                    <div className="p-5 sm:p-8">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                            {/* Avatar */}
                            <div className="relative group">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-2 border-[#681793]/50 overflow-hidden bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                                    {photoPreview ? (
                                        <img src={photoPreview} alt="Photo" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-2xl sm:text-3xl font-bold text-white">
                                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                                        </span>
                                    )}
                                </div>
                                <input type="file" ref={photoRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                                <button onClick={() => photoRef.current?.click()} disabled={uploadingPhoto}
                                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-lg bg-[#FF914D] text-white flex items-center justify-center hover:bg-[#FF7A26] transition-colors shadow-lg"
                                    data-testid="change-photo-button">
                                    {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                </button>
                            </div>

                            {/* Name + info */}
                            <div className="flex-1 min-w-0">
                                <h1 className="text-xl sm:text-2xl font-black text-foreground">
                                    {user?.first_name} {user?.last_name}
                                </h1>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                    <Badge className="bg-[#681793]/20 text-[#681793] border-[#681793]/30 text-xs capitalize">
                                        {user?.role === 'admin' ? 'Administrateur' : 'Agent'}
                                    </Badge>
                                    {profile.city && (
                                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                                            <MapPin className="w-3.5 h-3.5" /> {profile.city}
                                        </span>
                                    )}
                                    {profile.years_experience > 0 && (
                                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                                            <Briefcase className="w-3.5 h-3.5" /> {profile.years_experience} ans
                                        </span>
                                    )}
                                </div>
                                {profile.bio && (
                                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{profile.bio}</p>
                                )}
                            </div>

                            {/* Quick actions - Desktop */}
                            <div className="flex items-center gap-2 shrink-0">
                                <Button variant="outline" size="sm" onClick={() => setEditing(!editing)}
                                    className={`border-border text-sm ${editing ? 'border-[#FF914D]/50 text-[#FF914D]' : 'text-muted-foreground hover:text-foreground'}`}
                                    data-testid="toggle-edit-button">
                                    <Edit3 className="w-4 h-4 mr-1.5" />
                                    {editing ? 'Annuler' : 'Modifier'}
                                </Button>
                                <Link to={`/p/agent/${generateSlug(`${user?.first_name}-${user?.last_name}`)}`} target="_blank">
                                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-white" data-testid="view-public-profile">
                                        <ExternalLink className="w-4 h-4 mr-1.5" /> Public
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Stats cards */}
            <motion.div variants={fadeIn} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard icon={FileText} label="Mandats" value={stats.mandates} color="text-[#681793]" />
                <StatCard icon={Star} label="Note moyenne" value={stats.rating > 0 ? `${stats.rating.toFixed(1)}/5` : '-'} color="text-yellow-400" />
                <StatCard icon={Award} label="Badges" value={stats.badges} color="text-[#FF914D]" />
                <StatCard icon={Shield} label="XP Saison" value={stats.xp} color="text-green-400" />
            </motion.div>

            {/* Email personnel de récupération (le profil appartient à l'agent) */}
            <motion.div variants={fadeIn}>
                <PersonalEmailCard />
            </motion.div>

            {/* QR Code agent */}
            <motion.div variants={fadeIn}>
                <QRCodeSection kind="agent" />
            </motion.div>

            {/* Desktop: Two-column layout */}
            <div className="lg:grid lg:grid-cols-12 lg:gap-6">

                {/* Left: Quick info card */}
                <motion.div variants={fadeIn} className="lg:col-span-4 mb-6 lg:mb-0">
                    <div className="bg-card rounded-2xl border border-border p-5 space-y-5 lg:sticky lg:top-24">
                        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <User className="w-4 h-4 text-[#FF914D]" /> Informations
                        </h3>

                        <InfoRow icon={Mail} label="Email" value={user?.email} />
                        <InfoRow icon={Phone} label="Téléphone" value={profile.public_phone || 'Non renseigné'} muted={!profile.public_phone} />
                        <InfoRow icon={MapPin} label="Ville" value={profile.city || 'Non renseignée'} muted={!profile.city} />
                        <InfoRow icon={Globe} label="Zone" value={profile.zones || 'Non renseignée'} muted={!profile.zones} />
                        <InfoRow icon={Briefcase} label="Expérience" value={profile.years_experience ? `${profile.years_experience} ans` : 'Non renseignée'} muted={!profile.years_experience} />

                        {/* Specialties */}
                        {profile.specialties.length > 0 && (
                            <div>
                                <p className="text-xs text-muted-foreground mb-2">Spécialités</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {profile.specialties.map(s => (
                                        <Badge key={s} className="bg-[#681793]/10 text-[#681793] border-[#681793]/20 text-[10px] px-2 py-0.5">
                                            {s}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Quick links */}
                        <div className="border-t border-border pt-4 space-y-1.5">
                            <QuickLink to={`${basePath}/reputation`} label="Ma Réputation" icon={Star} />
                            <QuickLink to={`${basePath}/certifications`} label="Certifications" icon={Award} />
                            <QuickLink to={`${basePath}/leaderboard`} label="Classement" icon={CheckCircle} />
                        </div>
                    </div>
                </motion.div>

                {/* Right: Edit Form or Profile Display */}
                <motion.div variants={fadeIn} className="lg:col-span-8">
                    <AnimatePresence mode="wait">
                        {editing ? (
                            <motion.div key="edit" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                <EditForm
                                    user={user}
                                    profile={profile} setProfile={setProfile}
                                    onSave={handleSave} saving={saving}
                                    geocodeCity={geocodeCity} geocoding={geocoding}
                                    toggleSpecialty={toggleSpecialty}
                                />
                            </motion.div>
                        ) : (
                            <motion.div key="display" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                <ProfileDisplay profile={profile} onEdit={() => setEditing(true)} />
                                {/* Lot 1A.2 — visible aussi en lecture (insights + bouton aperçu public) */}
                                <ProfileReseauSection user={user} readOnly />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </motion.div>
    );
}

// Sub-components

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className="bg-card rounded-xl border border-border p-4 hover:border-white/15 transition-colors" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${color}`} />
                <span className="text-xs text-muted-foreground">{label}</span>
            </div>
            <p className="text-xl font-bold text-foreground">{value}</p>
        </div>
    );
}

function InfoRow({ icon: Icon, label, value, muted }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
                <p className={`text-sm truncate ${muted ? 'text-white/30 italic' : 'text-foreground'}`}>{value}</p>
            </div>
        </div>
    );
}

function QuickLink({ to, label, icon: Icon }) {
    return (
        <Link to={to} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-all group">
            <Icon className="w-4 h-4" />
            <span className="flex-1">{label}</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </Link>
    );
}

function ProfileDisplay({ profile, onEdit }) {
    return (
        <div className="space-y-6">
            {/* Bio card */}
            <div className="bg-card rounded-2xl border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Edit3 className="w-4 h-4 text-[#FF914D]" /> Présentation
                    </h3>
                    <Button variant="ghost" size="sm" onClick={onEdit} className="text-muted-foreground hover:text-white text-xs" data-testid="edit-bio-button">
                        Modifier
                    </Button>
                </div>
                {profile.bio ? (
                    <p className="text-sm text-muted-foreground leading-relaxed">{profile.bio}</p>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-sm text-white/30">Aucune présentation renseignée</p>
                        <Button variant="outline" size="sm" onClick={onEdit} className="mt-3 border-border text-muted-foreground hover:text-white">
                            <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Ajouter une bio
                        </Button>
                    </div>
                )}
            </div>

            {/* Coverage zone */}
            <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
                    <MapPin className="w-4 h-4 text-blue-400" /> Zone d'intervention
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-muted-foreground mb-1">Rayon</p>
                        <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#681793] to-[#FF914D] rounded-full"
                                    style={{ width: `${(profile.coverage_radius_km / 50) * 100}%` }} />
                            </div>
                            <span className="text-sm font-semibold text-foreground">{profile.coverage_radius_km} km</span>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground mb-1">Zones couvertes</p>
                        <p className="text-sm text-foreground">{profile.zones || 'Non renseigné'}</p>
                    </div>
                </div>
                {profile.latitude && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-green-400">
                        <CheckCircle className="w-3.5 h-3.5" /> Position GPS enregistrée
                    </div>
                )}
            </div>

            {/* Specialties */}
            <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
                    <Briefcase className="w-4 h-4 text-[#681793]" /> Spécialités
                </h3>
                {profile.specialties.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {profile.specialties.map(s => (
                            <Badge key={s} className="bg-[#681793]/10 text-[#681793] border-[#681793]/20 px-3 py-1 text-xs">
                                {s}
                            </Badge>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-white/30">Aucune spécialité sélectionnée</p>
                )}
            </div>
        </div>
    );
}

function EditForm({ user, profile, setProfile, onSave, saving, geocodeCity, geocoding, toggleSpecialty }) {
    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className="p-5 border-b border-border">
                <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <Edit3 className="w-5 h-5 text-[#FF914D]" /> Modifier mon profil
                </h3>
                <p className="text-sm text-muted-foreground mt-1">Les changements sont visibles sur Homly Réseau</p>
            </div>
            <div className="p-5 space-y-5">
                {/* City + geocode */}
                <div>
                    <Label className="text-muted-foreground">Ma ville</Label>
                    <div className="flex gap-2 mt-1">
                        <Input value={profile.city}
                            onChange={e => setProfile({ ...profile, city: e.target.value, latitude: null, longitude: null })}
                            placeholder="Carpentras" className="bg-white/5 border-border text-foreground flex-1"
                            onBlur={() => geocodeCity(profile.city)} data-testid="edit-city-input" />
                        <Button variant="outline" size="sm" onClick={() => geocodeCity(profile.city)} disabled={geocoding} className="border-border whitespace-nowrap">
                            {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />}
                            {geocoding ? '' : 'Localiser'}
                        </Button>
                    </div>
                    {profile.latitude && <p className="text-xs text-green-400 mt-1">Position GPS enregistrée</p>}
                </div>

                {/* Radius */}
                <div>
                    <Label className="text-muted-foreground">Zone d'intervention</Label>
                    <div className="mt-2">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-foreground font-medium">{profile.coverage_radius_km} km</span>
                            <span className="text-xs text-muted-foreground">autour de {profile.city || 'votre ville'}</span>
                        </div>
                        <input type="range" min="5" max="50" step="5" value={profile.coverage_radius_km}
                            onChange={e => setProfile({ ...profile, coverage_radius_km: parseInt(e.target.value) })}
                            className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-[#681793]" />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>5 km</span><span>25 km</span><span>50 km</span>
                        </div>
                    </div>
                </div>

                {/* Phone */}
                <div>
                    <Label className="text-muted-foreground">Téléphone public</Label>
                    <Input value={profile.public_phone}
                        onChange={e => setProfile({ ...profile, public_phone: e.target.value })}
                        placeholder="06 00 00 00 00" className="bg-white/5 border-border text-foreground"
                        data-testid="edit-phone-input" />
                </div>

                {/* Zones */}
                <div>
                    <Label className="text-muted-foreground">Zones couvertes</Label>
                    <Input value={profile.zones}
                        onChange={e => setProfile({ ...profile, zones: e.target.value })}
                        placeholder="Carpentras, Avignon, Orange..."
                        className="bg-white/5 border-border text-foreground" />
                    <p className="text-xs text-muted-foreground mt-1">Séparez les villes par des virgules</p>
                </div>

                {/* Bio */}
                <div>
                    <Label className="text-muted-foreground">Présentation</Label>
                    <textarea value={profile.bio}
                        onChange={e => setProfile({ ...profile, bio: e.target.value })}
                        placeholder="Agent immobilier spécialisé dans..."
                        rows={4}
                        className="w-full mt-1 px-3 py-2 bg-white/5 border border-border rounded-xl text-foreground placeholder:text-muted-foreground text-sm resize-none focus:border-[#681793] focus:ring-1 focus:ring-[#681793] transition-all"
                        data-testid="edit-bio-textarea" />
                </div>

                {/* Experience */}
                <div>
                    <Label className="text-muted-foreground">Années d'expérience</Label>
                    <Input type="number" value={profile.years_experience}
                        onChange={e => setProfile({ ...profile, years_experience: e.target.value })}
                        placeholder="5" className="bg-white/5 border-border text-foreground w-32" />
                </div>

                {/* Specialties */}
                <div>
                    <Label className="text-muted-foreground">Spécialités</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {SPECIALTIES.map(spec => (
                            <button key={spec} onClick={() => toggleSpecialty(spec)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                    profile.specialties.includes(spec)
                                        ? 'bg-[#681793] text-white'
                                        : 'bg-white/5 text-muted-foreground hover:bg-white/10'
                                }`} data-testid={`specialty-${spec.toLowerCase().replace(/\s/g, '-')}`}>
                                {spec}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Save */}
                <div className="flex justify-end pt-4 border-t border-border">
                    <Button onClick={onSave} disabled={saving}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                        data-testid="save-profile-button">
                        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>
                            : <><Save className="w-4 h-4 mr-2" />Enregistrer le profil</>}
                    </Button>
                </div>

                {/* Lot 1A — Section Profil Réseau Homly (additive, ne touche pas l'existant) */}
                <ProfileReseauSection user={user} />
            </div>
        </div>
    );
}

export default ProfilePage;
